import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';

import { getReduxStorageUtil } from '../../../main/utils/storage/redux-storage.util.ts';

import { AUTH_SLICE_NAME, AuthSliceState } from './auth.slice.types.ts';
import { resetPasswordAndSignInThunkExtraReducers } from './thunks/reset-password-and-sign-in.thunk.ts';
import { sendPasswordResetCodeThunkExtraReducers } from './thunks/send-password-reset-code.thunk.ts';
import { signInThunkExtraReducers } from './thunks/sign-in.thunk.ts';
import { signOutThunkExtraReducers } from './thunks/sign-out.thunk.ts';
import { signUpThunkExtraReducers } from './thunks/sign-up.thunk.ts';
import { validatePasswordResetCodeThunkExtraReducers } from './thunks/validate-password-reset-code.thunk.ts';

export const AUTH_SLICE_INITIAL_STATE: AuthSliceState = {
  isLoading: false,
  isSignOutLoading: false,
  isRememberMe: false,
  hasUserSignedIn: false,
  forgotPasswordEmail: undefined,
  forgotPasswordCode: undefined,
  formUserName: '',
  formUserEmail: '',
  formUserPassword: '',
  preFilledLoginEmail: undefined,
  signUpExistingUserErrorId: undefined,
};

const persistKeys: (keyof AuthSliceState)[] = ['isRememberMe', 'hasUserSignedIn'];

const persistConfig = {
  key: AUTH_SLICE_NAME,
  storage: getReduxStorageUtil(),
  whitelist: persistKeys,
} as PersistConfig<AuthSliceState>;

const authSlice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState: AUTH_SLICE_INITIAL_STATE,
  reducers: {
    resetAuthSlice: (state) => {
      return {
        ...AUTH_SLICE_INITIAL_STATE,
        hasUserSignedIn: state.hasUserSignedIn,
      };
    },
    resetAuthFormData: (state) => {
      state.formUserName = '';
      state.formUserEmail = '';
      state.formUserPassword = '';
      state.forgotPasswordEmail = '';
      state.forgotPasswordCode = '';
    },
    setIsRememberMe: (state, action: PayloadAction<boolean>) => {
      state.isRememberMe = action.payload;
    },
    setHasUserSignedIn: (state, action: PayloadAction<boolean>) => {
      state.hasUserSignedIn = action.payload;
    },
    setForgotPasswordEmail: (state, action: PayloadAction<string | undefined>) => {
      state.forgotPasswordEmail = action.payload;
    },
    setForgotPasswordCode: (state, action: PayloadAction<string>) => {
      state.forgotPasswordCode = action.payload;
    },
    setFormUserName: (state, action: PayloadAction<string>) => {
      state.formUserName = action.payload;
    },
    setFormUserEmail: (state, action: PayloadAction<string>) => {
      state.formUserEmail = action.payload;
    },
    setFormUserPassword: (state, action: PayloadAction<string>) => {
      state.formUserPassword = action.payload;
    },
    setPreFilledLoginEmail: (state, action: PayloadAction<string | undefined>) => {
      state.preFilledLoginEmail = action.payload;
    },
    setSignUpExistingUserErrorId: (state, action: PayloadAction<string | undefined>) => {
      state.signUpExistingUserErrorId = action.payload;
    },
  },
  extraReducers: (builder) => {
    signUpThunkExtraReducers(builder);
    signInThunkExtraReducers(builder);
    sendPasswordResetCodeThunkExtraReducers(builder);
    validatePasswordResetCodeThunkExtraReducers(builder);
    resetPasswordAndSignInThunkExtraReducers(builder);
    signOutThunkExtraReducers(builder);
  },
});

export const {
  setIsRememberMe,
  setHasUserSignedIn,
  setForgotPasswordEmail,
  setForgotPasswordCode,
  setFormUserName,
  setFormUserEmail,
  setFormUserPassword,
  setPreFilledLoginEmail,
  setSignUpExistingUserErrorId,
  resetAuthSlice,
  resetAuthFormData,
} = authSlice.actions;

export const AuthSlice = persistReducer(persistConfig, authSlice.reducer);
